import { useQuery, useQueryClient } from '@tanstack/react-query';
import { format, isValid, parseISO } from 'date-fns';
import { useRef, useState } from 'react';
import { Button, Container, Loader } from 'rsuite';
import { ActionFeedbackDialog } from '../../../components';
import { AlertWithTimestamp } from '../../../components/AlertWithTimestamp';
import { LoadingSpiner } from '../../../components/LoadingSpinner';
import { extractApiErrorMessage } from '../../../utils/extractApiErrorMessage';
import { TabelaPrecosUploadModal } from './TabelaPrecosUploadModal';
import {
  downloadPriceTableTemplate,
  getPriceTableLastUpdate,
  PRICE_TABLE_LAST_UPDATE_QUERY_KEY,
  uploadPriceTable,
} from './services';

type FeedbackState = { type: 'processing' } | { type: 'success'; message: string; totalProcessed?: number } | { type: 'error'; message: string } | null;

const INVALID_EXTENSION_MESSAGE = 'Formato inválido. Envie um arquivo .xlsx, .xls ou .csv.';

export const formatPriceTableLastUpdate = (lastUpdate?: string | null) => {
  if (!lastUpdate) return null;

  const parsedDate = parseISO(lastUpdate);
  return isValid(parsedDate) ? format(parsedDate, 'dd/MM/yyyy, HH:mm') : null;
};

const TabelaPrecos = () => {
  const queryClient = useQueryClient();
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<FeedbackState>(null);
  const isUploadingRef = useRef(false);
  const { data: lastUpdateData } = useQuery({
    queryKey: PRICE_TABLE_LAST_UPDATE_QUERY_KEY,
    queryFn: getPriceTableLastUpdate,
    refetchInterval: 60_000,
  });
  const formattedLastUpdate = formatPriceTableLastUpdate(lastUpdateData?.lastUpdate);

  const resetUpload = () => {
    setSelectedFile(null);
    setFileError(null);
  };

  const openUpload = () => {
    resetUpload();
    setShowUploadModal(true);
  };

  const closeUpload = () => {
    setShowUploadModal(false);
    resetUpload();
  };

  const handleFileChange = (file: File | null) => {
    setSelectedFile(file);
    setFileError(null);
  };

  const handleInvalidFile = () => {
    setSelectedFile(null);
    setFileError(INVALID_EXTENSION_MESSAGE);
  };

  const handleDownloadTemplate = async () => {
    try {
      await downloadPriceTableTemplate();
    } catch (error) {
      AlertWithTimestamp({
        message: extractApiErrorMessage(error) || 'Não foi possível baixar o modelo de arquivo.',
      });
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || isUploadingRef.current) return;

    isUploadingRef.current = true;
    setShowUploadModal(false);
    setFeedback({ type: 'processing' });

    try {
      const response = await uploadPriceTable(selectedFile);
      void queryClient.invalidateQueries({ queryKey: PRICE_TABLE_LAST_UPDATE_QUERY_KEY });
      setFeedback({
        type: 'success',
        message: response.data?.message || 'Arquivo processado com sucesso.',
        totalProcessed: response.data?.totalProcessed,
      });
    } catch (error) {
      setFeedback({
        type: 'error',
        message: extractApiErrorMessage(error) || 'Não foi possível processar o arquivo. Tente novamente.',
      });
    } finally {
      isUploadingRef.current = false;
    }
  };

  const closeFeedback = () => {
    setFeedback(null);
    resetUpload();
  };

  const retryUpload = () => {
    setFeedback(null);
    setShowUploadModal(true);
  };

  return (
    <>
      <Container className="main-container">
        <LoadingSpiner size="md" />
        <div className="tabela-precos-page">
          <header className="price-table-header">
            <div className="price-table-header-content">
              <h1>Tabela de Preços</h1>
              <p>
                A tabela de preços garante transparência e confiança para os clientes, além de automatizar e reduzir erros na precificação de produtos com
                preços tabelados ou acordados com a indústria.
              </p>
              {formattedLastUpdate ? <span>Última atualização: {formattedLastUpdate}</span> : null}
            </div>
          </header>

          <div className="price-table-content">
            <section className="price-table-explanation">
              <div className="price-table-explanation-content">
                <h2>Como funciona a tabela de preços</h2>
                <div className="price-table-explanation-copy">
                  <p>
                    A <strong>tabela de preços</strong> funciona como referência para os valores dos produtos. Ela define limites de precificação, com preço
                    mínimo e máximo, ou um valor fixo recomendado, o preço sugerido.
                  </p>
                  <p>
                    Quando um produto está configurado com os valores da tabela, o IPA verifica se o preço atual está dentro da faixa definida ou corresponde ao
                    preço sugerido. Se estiver fora desses parâmetros, o produto recebe uma indicação visual no gerenciador alertando para revisão manual.
                  </p>
                  <p>
                    Quando o produto está configurado com um preço sugerido, <strong>as regras de precificação deixam de sugerir preços para ele</strong> — o
                    valor da tabela passa a ser a referência. Para esse caso, também é possível definir uma data final de vigência: a partir dessa data, o
                    produto volta a receber sugestões de preço pelas regras normalmente.
                  </p>
                  <p>
                    Para atualizar, basta enviar o arquivo. Para remover um item ou toda a tabela de preços, entre em contato com o suporte.
                    <br />
                    <br />
                    Se tiver dúvidas, fale com a gente.
                  </p>
                </div>
              </div>
            </section>

            <section className="price-table-upload-card">
              <h2>Envie sua tabela de preços atualizada</h2>
              <div className="upload-instructions">
                <p>
                  Envie um arquivo <strong>.xlsx, .xls</strong> ou <strong>.csv</strong> com as colunas obrigatórias Cod Produto e Cod Loja. As colunas de preço
                  (Preço Sugerido, Preço Mínimo e Preço Máximo) e Data Fim Vigência são opcionais, mas pelo menos uma coluna de preço deve estar preenchida.
                </p>
                <p>
                  <button type="button" className="download-modelo" onClick={handleDownloadTemplate}>
                    Baixe aqui
                  </button>{' '}
                  o modelo de planilha.
                </p>
              </div>
              <Button className="botao-arquivo" appearance="primary" id="send-price-table-btn" onClick={openUpload}>
                ENVIAR ARQUIVO
              </Button>
            </section>
          </div>

        </div>
      </Container>

      <TabelaPrecosUploadModal
        show={showUploadModal}
        file={selectedFile}
        errorMessage={fileError}
        onFileChange={handleFileChange}
        onInvalidFile={handleInvalidFile}
        onDownloadTemplate={handleDownloadTemplate}
        onSubmit={handleUpload}
        onHide={closeUpload}
      />

      <ActionFeedbackDialog
        open={feedback?.type === 'processing'}
        type="other"
        icon={<Loader size="lg" />}
        title="Processando seu arquivo"
        description="Estamos validando o arquivo e atualizando os preços dos produtos por loja. Leva alguns instantes."
        dismissible={false}
      />

      <ActionFeedbackDialog
        open={feedback?.type === 'success'}
        type="success"
        title="Arquivo processado com sucesso"
        description={
          feedback?.type === 'success' ? (
            <>
              <p>{feedback.message}</p>
              {typeof feedback.totalProcessed === 'number' ? (
                <p>
                  <strong>Total de registros processados:</strong> {feedback.totalProcessed}
                </p>
              ) : null}
            </>
          ) : (
            ''
          )
        }
        onClose={closeFeedback}
        primaryAction={{
          label: 'FECHAR',
          onClick: closeFeedback,
        }}
      />

      <ActionFeedbackDialog
        open={feedback?.type === 'error'}
        type="warning"
        title="Não foi possível processar o arquivo"
        description={feedback?.type === 'error' ? feedback.message : ''}
        onClose={closeFeedback}
        primaryAction={{
          label: 'TENTAR NOVAMENTE',
          onClick: retryUpload,
        }}
      />
    </>
  );
};

export default TabelaPrecos;
